import React from 'react'
import {get, has, isNil, isEmpty} from 'lodash'
import IconMenu from 'material-ui/IconMenu'
import IconButton from 'material-ui/IconButton'
import MenuItem from 'material-ui/MenuItem'
import iconNames from 'theme/icons'
import Icon from 'components/icon'
import {getPartType} from 'helpers/parts'
import {parseCssSelector} from 'helpers/css'

export const getBreadcrumbsFromParents = (partCategories, selectedCategoryId, search) => {
  const partCategory = findPartCategory(partCategories, selectedCategoryId)

  if (isNil(partCategory))
    return []

  let breadcrumbs = [{label: partCategory.name, category: partCategory}]

  if (search) {
    breadcrumbs.unshift({label: '(Search Results)'})
  } else if (partCategory.parentId) {
    breadcrumbs = [
      ...getBreadcrumbsFromParents(partCategories, partCategory.parentId, search),
      ...breadcrumbs,
    ]
  } else {
    breadcrumbs.unshift({label: 'All Price Lists'})
  }
  return breadcrumbs
}

const findPartCategory = (partCategories, categoryId) => {
  return partCategories.find(category => category.id === categoryId)
}

export const findRootPartCategory = (partCategories, categoryId) => {
  const partCategory = findPartCategory(partCategories, categoryId)
  const parent = get(partCategory, 'parentId', null)

  if (!isNil(parent)) {
    return findRootPartCategory(partCategories, parent)
  }

  return partCategory
}

export const findParentCategories = (partCategories, categoryId, parents = []) => {
  const partCategory = findPartCategory(partCategories, categoryId)
  const parent = get(partCategory, 'parentId', null)

  if (!isNil(parent)) {
    return findParentCategories(partCategories, parent, [...parents, parent])
  }

  return parents
}

export const isPartCategoryWithinTargetParents = (partCategories, targetCategoryId, id) => {
  const parents = findParentCategories(partCategories, targetCategoryId)

  return isEmpty(parents)
    ? false
    : parents.indexOf(id) >= 0
}

export const getBreadcrumbs = (parentCategories, search) => {
  const breadcrumbs = parentCategories.map(category => ({label: category.name, category}))

  if (search) {
    breadcrumbs.unshift({label: '(Search Results)'})
  } else {
    breadcrumbs.unshift({label: 'All Price Lists'})
  }

  return breadcrumbs
}

export const selectBreadcrumb = (
  breadcrumb,
  search,
  onSearch,
  onSelectCategory,
  onSelectPriceList,
  onSelectAllPriceLists,
  partCategories
) => {
  if (breadcrumb.priceList) {
    onSelectPriceList(breadcrumb.priceList.id)
  } else if (breadcrumb.category) {
    onSelectCategory(breadcrumb.category, false, partCategories)
  } else if (search) {
    onSearch(search)
  } else {
    onSelectAllPriceLists()
  }
}

export const getTreeTitle = (priceList, category) => {
  if (category) return category.name
  if (priceList) return priceList.name
  return 'All Price Lists'
}

export const selectFolder = (folder, onSelectCategory, onSelectPriceList, partCategories) => {
  if (folder.priceList) {
    onSelectPriceList(folder.priceList.id)
  } else if (folder.category) {
    onSelectCategory(folder.category, true, partCategories)
  }
}

export const getCategoryTreeItem = (category) => {
  const partType = getPartType(category.partType)
  const icon = partType ? partType.icon : null
  return {label: category.name, id: category.id, category, icon}
}

export const getPriceListTreeItem = (priceList) =>
  ({label: priceList.name, id: -priceList.id, priceList, icon: iconNames.priceList})

export const getTreeItems = (priceListId, categoryId, search, categories) => {
  return isNil(categories)
    ? []
    : categories.map(getCategoryTreeItem)
}

export const isInRootPriceList = (priceList, category) => {
  return isNil(priceList) && isNil(category)
}

export const isInPriceList = (priceList, category) => {
  return isInRootPriceList(priceList, category) || isNil(category)
}

export const sortFolders = (folders = [], category, direction) => {
  if (isNil(category) && (isNil(direction) || direction === false)) {
    const priceListOrderPath = 'category.priceList.order'
    return folders.sort((a, b) => {
      return get(a, priceListOrderPath, 0) - get(b, priceListOrderPath, 0)
    })
  }

  return folders
}

export const getNodeForCategory = (folder, nodes) => {
  return nodes
    .find(nodes => nodes.category.id === folder.id)
}

export const getFolderForCheckedNode = (folder, checkedNodes) => {
  const checkedNode = getNodeForCategory(folder, checkedNodes)

  if (!isNil(checkedNode)) {
    folder['isChecked'] = get(checkedNode, 'isChecked', true)
    return folder
  }

  return folder
}

export const getFolderForDisabledNode = (folder, disabledNodes) => {
  const disabledNode = getNodeForCategory(folder, disabledNodes)

  if (!isNil(disabledNode)) {
    folder['isDisabled'] = get(disabledNode, 'isDisabled', true)
    return folder
  }

  return folder
}

export const getFolderIcon = (folder, folderIcon) => {
  return (has(folder, 'icon') && isNil(folder.icon) === false)
    ? folder.icon
    : folderIcon && isNil(folderIcon) === false
      ? folderIcon
      : 'folder_filled'
}

export const getActions = (
  folder,
  onShowDeletePartCategoryDialog,
  onShowMovePartCategoryDialog,
  onShowPartCategoryDialog,
  actionsAllowed
) => {
  if (actionsAllowed && get(folder, 'category', false)) {
    return [
      {
        text: 'Edit',
        icon: iconNames.edit,
        onClick: (e, category) => {
          e.stopPropagation()
          onShowPartCategoryDialog(category, true)
        },
      },
      {
        text: 'Move',
        icon: iconNames.moveStockFilled,
        onClick: (e, category) => {
          e.stopPropagation()
          onShowMovePartCategoryDialog(category)
        },
      },
      {
        text: 'Delete',
        icon: iconNames.delete,
        onClick: (e, category) => {
          e.stopPropagation()
          onShowDeletePartCategoryDialog(category)
        },
      },
    ]
  }
  return []
}

export const mapPriceListToCategory = (priceList) => {
  return {
    ...priceList.rootCategory,
    priceList: {
      visible: priceList.visible,
      createdDate: priceList.createdDate,
      name: priceList.name,
      modifiedDate: priceList.modifiedDate,
      guid: priceList.guid,
      id: priceList.id,
      order: priceList.order,
    },
  }
}

export const getPartCategoryActionsMenu = (actions, node, mustHaveParent) => {
  if (mustHaveParent && isNil(get(node, 'parentKey', null)))
    return

  if (isEmpty(actions))
    return

  if (get(node, 'key', 0) === 0)
    return

  if (isNil(get(node, 'parentKey', null))) {
    actions = [actions[0]]
  }

  return (
    <IconMenu
      iconButtonElement={<IconButton><Icon name={iconNames.more}/></IconButton>}
      onClick={(e) => e.stopPropagation()}
      anchorOrigin={{horizontal: 'left', vertical: 'top'}}
      targetOrigin={{horizontal: 'left', vertical: 'top'}}
      className={`optionMenu ${parseCssSelector(node.label)}`}
      useLayerForClickAway={true}
    >
      {actions.map(action =>
        <MenuItem
          key={action.text}
          leftIcon={<Icon name={action.icon}/>}
          primaryText={action.text}
          className={action.text}
          onClick={(e) => action.onClick(e, node)}
        />
      )}
    </IconMenu>
  )
}
